import { ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { isTwoFactorChallenge, useLogin, useLoginTotp } from '../api/auth.js';
import { Button, Card, ErrorNote, Input, Label } from '../components/ui.js';

export function LoginPage({ panelName }: { panelName: string }) {
  const login = useLogin();
  const loginTotp = useLoginTotp();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');

  const challenge = login.data != null && isTwoFactorChallenge(login.data);

  const header = (
    <div className="mb-8 flex flex-col items-center gap-3">
      <div className="flex size-12 items-center justify-center rounded-xl bg-brand-600 text-lg font-bold text-white">
        CP
      </div>
      <h1 className="text-xl font-semibold">{panelName}</h1>
    </div>
  );

  // Step 2: second factor.
  if (challenge) {
    return (
      <div className="flex h-full items-center justify-center bg-slate-50 p-6 dark:bg-slate-950">
        <div className="w-full max-w-sm">
          {header}
          <Card className="p-6">
            <div className="mb-4 flex flex-col items-center gap-2 text-center">
              <ShieldCheck className="size-8 text-brand-600" />
              <h2 className="text-base font-semibold">Two-factor authentication</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Enter the 6-digit code from your authenticator, or a recovery code.
              </p>
            </div>
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                loginTotp.mutate({ code });
              }}
            >
              <div>
                <Label htmlFor="totp">Authentication code</Label>
                <Input
                  id="totp"
                  autoFocus
                  autoComplete="one-time-code"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </div>
              {loginTotp.error != null && <ErrorNote error={loginTotp.error} />}
              <Button type="submit" className="w-full" loading={loginTotp.isPending}>
                Verify
              </Button>
              <button
                type="button"
                onClick={() => {
                  login.reset();
                  setCode('');
                }}
                className="w-full text-center text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400"
              >
                Back to sign in
              </button>
            </form>
          </Card>
        </div>
      </div>
    );
  }

  // Step 1: password.
  return (
    <div className="flex h-full items-center justify-center bg-slate-50 p-6 dark:bg-slate-950">
      <div className="w-full max-w-sm">
        {header}
        <Card className="p-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              login.mutate({ email, password });
            }}
            className="space-y-4"
          >
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {login.error != null && <ErrorNote error={login.error} />}
            <Button type="submit" className="w-full" loading={login.isPending}>
              Sign in
            </Button>
            <Link to="/forgot-password" className="block text-center text-sm text-brand-600">Forgot password?</Link>
          </form>
        </Card>
      </div>
    </div>
  );
}
